import { type ErrorCodes } from '@/shared/graphql/legacy/generated'
import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'

export interface Upload {
  _id: string
  id?: string
  sessionId: number
  fileName: string
  percentage: number
  timeLeft: number
  error?: ErrorCodes
  canceled?: boolean
  abortController: AbortController
  size: number
}

export type UploadInput = Omit<Upload, 'sessionId'>

export enum UploadEvent {
  None = 'none',
  Uploaded = 'uploaded',
  Canceled = 'canceled',
  Error = 'error',
}

let sessionId = 0

export const useUploadStore = defineStore('upload', () => {
  const uploads = ref([]) as Ref<Upload[]>
  const showUploadBar = ref(false)
  const finalUploadEvent = ref<UploadEvent>(UploadEvent.None)

  const currentSessionUploads = computed(() =>
    uploads.value.filter((upload) => upload.sessionId === sessionId)
  )

  const uploadsInProgress = computed(() =>
    uploads.value.filter((upload) => {
      return (
        (upload.timeLeft || upload.percentage > 0) &&
        upload.percentage < 100 &&
        !upload.error &&
        !upload.canceled
      )
    })
  )

  const allUploadsSettled = computed(
    () =>
      uploads.value.length > 0 &&
      uploads.value.every(
        (upload) =>
          (upload.percentage === 100 && upload.timeLeft === 0) || upload.error
      )
  )

  const addUpload = (upload: UploadInput) => {
    uploads.value.push({ ...upload, sessionId })
  }

  const updateUploadProgress = (
    id: string,
    { timeLeft, percentage }: { timeLeft: number; percentage: number }
  ) => {
    const upload = uploads.value.find((u) => u._id === id)

    if (!upload) return

    upload.percentage = percentage

    if (upload.percentage === 100) {
      upload.timeLeft = 0

      if (allUploadsSettled.value) {
        finalUploadEvent.value = UploadEvent.Uploaded
      }
    } else {
      upload.timeLeft = timeLeft
    }
  }

  const setUploadError = (
    id: string,
    { error, canceled }: { error: ErrorCodes; canceled?: boolean }
  ) => {
    const upload = uploads.value.find((upload) => upload._id === id)

    if (!upload || upload.error) return

    upload.error = error

    if (canceled) {
      upload.canceled = true

      if (allUploadsSettled.value) {
        finalUploadEvent.value = UploadEvent.Canceled
      }

      return
    }

    if (allUploadsSettled.value) {
      finalUploadEvent.value = UploadEvent.Error
    }
  }

  const startNewSession = () => {
    sessionId++
    finalUploadEvent.value = UploadEvent.None
  }

  return {
    uploads,
    currentSessionUploads,
    uploadsInProgress,
    allUploadsSettled,
    showUploadBar,
    finalUploadEvent,
    addUpload,
    updateUploadProgress,
    setUploadError,
    startNewSession,
  }
})
